import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Share2, Settings, FileText, MapPin, User, Calendar,
  Clock, Plus, ChevronRight, Save, CheckCircle2,
  MessageSquare, Camera, Star, X, Image, Pause, Play,
  AlertTriangle, Loader2, Trash2, RotateCcw
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const itemTypeConfig = {
  obligatoire: { label: 'OBLIGATOIRE', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  optionnel: { label: 'OPTIONNEL', color: 'bg-slate-100 text-slate-600 border-slate-300' },
  critique: { label: 'CRITIQUE', color: 'bg-red-100 text-red-700 border-red-300' }
};

export default function ChecklistExecutionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [notes, setNotes] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isRunning, setIsRunning] = useState(true);
  const [recentPhotos, setRecentPhotos] = useState([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [currentPhotoTarget, setCurrentPhotoTarget] = useState(null); // { sectionIdx, itemIdx }
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchChecklist();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      stopCamera();
    };
  }, [id]);

  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRunning]);

  const fetchChecklist = async () => {
    try {
      const res = await fetch(`${API_URL}/api/checklists/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.items && !data.sections) {
          data.sections = [{
            id: 'main',
            title: 'Points de contrôle',
            description: 'Vérification des éléments',
            items: data.items.map((item, idx) => ({
              ...item,
              id: item.id || `item-${idx}`,
              type: item.type || 'obligatoire',
              responseType: item.responseType || 'conformity',
              response: item.response || null,
              comment: item.comment || '',
              photos: item.photos || []
            }))
          }];
        }
        // Collect all existing photos as recent photos
        const allPhotos = [];
        data.sections?.forEach(section => {
          section.items?.forEach(item => {
            if (item.photos) {
              allPhotos.push(...item.photos);
            }
          });
        });
        setRecentPhotos(allPhotos.slice(-6)); // Keep last 6
        setChecklist(data);
        setNotes(data.notes || '');
        setElapsedTime(data.elapsed_time || 0);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (finish = false) => {
    setSaving(true);
    try {
      const updateData = {
        sections: checklist.sections,
        notes: notes,
        elapsed_time: elapsedTime,
        status: finish ? 'completed' : 'in_progress'
      };
      
      const res = await fetch(`${API_URL}/api/checklists/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });
      
      if (res.ok) {
        toast.success(finish ? 'Contrôle terminé' : 'Brouillon enregistré');
        if (finish) {
          navigate('/forms');
        }
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleItemResponse = (sectionIdx, itemIdx, response) => {
    const newSections = [...checklist.sections];
    newSections[sectionIdx].items[itemIdx].response = response;
    newSections[sectionIdx].items[itemIdx].checked = response === 'conforme';
    setChecklist({ ...checklist, sections: newSections });
  };

  const handleItemComment = (sectionIdx, itemIdx, comment) => {
    const newSections = [...checklist.sections];
    newSections[sectionIdx].items[itemIdx].comment = comment;
    setChecklist({ ...checklist, sections: newSections });
  };

  const toggleItemComment = (sectionIdx, itemIdx) => {
    const newSections = [...checklist.sections];
    newSections[sectionIdx].items[itemIdx].showComment = !newSections[sectionIdx].items[itemIdx].showComment;
    setChecklist({ ...checklist, sections: newSections });
  };

  const handleSubItemToggle = (sectionIdx, itemIdx, subIdx) => {
    const newSections = [...checklist.sections];
    const subItems = newSections[sectionIdx].items[itemIdx].subItems || [];
    if (subItems[subIdx]) {
      subItems[subIdx].checked = !subItems[subIdx].checked;
      newSections[sectionIdx].items[itemIdx].subItems = subItems;
      setChecklist({ ...checklist, sections: newSections });
    }
  };

  const handleRating = (sectionIdx, itemIdx, rating) => {
    const newSections = [...checklist.sections];
    newSections[sectionIdx].items[itemIdx].rating = rating;
    setChecklist({ ...checklist, sections: newSections });
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' }, // Use back camera on mobile
        audio: false 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Impossible d\'accéder à la caméra. Vérifiez les permissions.');
      // Fallback to file input
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      setCameraOpen(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      stopCamera();
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const openCamera = (sectionIdx, itemIdx) => {
    setCurrentPhotoTarget({ sectionIdx, itemIdx });
    setCapturedImage(null);
    setCameraOpen(true);
    setTimeout(() => startCamera(), 100);
  };

  const closeCamera = () => {
    stopCamera();
    setCameraOpen(false);
    setCapturedImage(null);
    setCurrentPhotoTarget(null);
  };

  // Handle file input (fallback for desktop or permission denied)
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedImage(e.target.result);
        setCameraOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload and save photo
  const savePhoto = async () => {
    if (!capturedImage || !currentPhotoTarget) return;
    
    setUploadingPhoto(true);
    try {
      // Convert base64 to blob
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      
      // Create form data for upload
      const formData = new FormData();
      formData.append('file', blob, `photo_${Date.now()}.jpg`);
      
      // Upload to server
      const uploadRes = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      
      if (uploadRes.ok) {
        const { url } = await uploadRes.json();
        const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
        
        // Add photo to item
        const { sectionIdx, itemIdx } = currentPhotoTarget;
        const newSections = [...checklist.sections];
        if (!newSections[sectionIdx].items[itemIdx].photos) {
          newSections[sectionIdx].items[itemIdx].photos = [];
        }
        newSections[sectionIdx].items[itemIdx].photos.push(fullUrl);
        setChecklist({ ...checklist, sections: newSections });
        
        // Add to recent photos
        setRecentPhotos(prev => [...prev.slice(-5), fullUrl]);
        
        toast.success('Photo ajoutée');
        closeCamera();
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload de la photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Delete photo from item
  const deletePhoto = (sectionIdx, itemIdx, photoIdx) => {
    const newSections = [...checklist.sections];
    const removedPhoto = newSections[sectionIdx].items[itemIdx].photos[photoIdx];
    newSections[sectionIdx].items[itemIdx].photos.splice(photoIdx, 1);
    setChecklist({ ...checklist, sections: newSections });
    
    // Remove from recent photos too
    setRecentPhotos(prev => prev.filter(p => p !== removedPhoto));
    toast.success('Photo supprimée');
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateProgress = () => {
    if (!checklist?.sections) return { percent: 0, checked: 0, total: 0, reserves: 0 };
    let total = 0, checked = 0, reserves = 0;
    checklist.sections.forEach(section => {
      section.items?.forEach(item => {
        total++;
        if (item.response === 'conforme' || item.checked) checked++;
        if (item.response === 'non_conforme') reserves++;
      });
    });
    return { 
      percent: total > 0 ? Math.round((checked / total) * 100) : 0,
      checked, total, reserves
    };
  };

  const currentSection = checklist?.sections?.[currentSectionIndex];
  const progress = calculateProgress();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-slate-500">Checklist non trouvée</p>
        <Button onClick={() => navigate('/forms')} className="mt-4">Retour</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Hidden file input for fallback */}
      <input 
        type="file" 
        ref={fileInputRef}
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/forms')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au tableau de bord
            </button>
            <div className="text-center">
              <h1 className="text-xl font-bold text-slate-900">{checklist.title}</h1>
              <div className="flex items-center justify-center gap-4 text-sm text-slate-500 mt-1">
                {checklist.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />{checklist.location}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />{checklist.created_by_name}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {format(new Date(checklist.created_at), 'dd MMM yyyy', { locale: fr })}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-1" />Partager
              </Button>
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-1" />Paramètres
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate(`/checklist/report/${id}`)}>
                <FileText className="w-4 h-4 mr-1" />Rapport
              </Button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4 flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <span>Progression: <strong>{progress.percent}%</strong></span>
              <span>Points vérifiés: <strong>{progress.checked}/{progress.total}</strong></span>
              {progress.reserves > 0 && (
                <span className="text-red-600">Réserves: <strong>{progress.reserves}</strong></span>
              )}
            </div>
            <div className="flex items-center gap-2 text-slate-500">
              <Clock className="w-4 h-4" />
              Temps: {formatTime(elapsedTime)}
            </div>
          </div>
          <Progress value={progress.percent} className="h-2 mt-2" />
          
          {/* Section Tabs */}
          {checklist.sections && checklist.sections.length > 1 && (
            <div className="flex gap-4 mt-4 overflow-x-auto pb-2">
              {checklist.sections.map((section, idx) => {
                const sectionProgress = section.items?.filter(i => i.response || i.checked).length || 0;
                const sectionTotal = section.items?.length || 0;
                const isComplete = sectionProgress === sectionTotal && sectionTotal > 0;
                const isCurrent = idx === currentSectionIndex;
                
                return (
                  <button
                    key={section.id || idx}
                    onClick={() => setCurrentSectionIndex(idx)}
                    className={`flex-shrink-0 pb-2 border-b-2 transition-colors ${
                      isCurrent ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{section.title}</span>
                      {isComplete && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Terminé</Badge>
                      )}
                      {isCurrent && !isComplete && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">En cours</Badge>
                      )}
                    </div>
                    <div className={`h-1 mt-1 rounded ${isComplete ? 'bg-emerald-500' : isCurrent ? 'bg-amber-400' : 'bg-slate-200'}`} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Items */}
          <div className="lg:col-span-2 space-y-4">
            {/* Section Header */}
            {currentSection && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">{currentSection.title}</h2>
                      {currentSection.description && (
                        <p className="text-slate-500 mt-1">{currentSection.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Plus className="w-4 h-4 mr-1" />Ajouter un point
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setCurrentSectionIndex(prev => Math.min(prev + 1, (checklist.sections?.length || 1) - 1))}>
                        <ChevronRight className="w-4 h-4 mr-1" />Passer la section
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Items */}
            {currentSection?.items?.map((item, itemIdx) => (
              <Card key={item.id || itemIdx} className={`${item.response === 'non_conforme' ? 'border-red-200 bg-red-50/30' : item.response === 'conforme' ? 'border-emerald-200 bg-emerald-50/30' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm">
                        {itemIdx + 1}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{item.text}</h3>
                        {item.description && (
                          <p className="text-slate-500 text-sm mt-1">{item.description}</p>
                        )}
                      </div>
                    </div>
                    <Badge className={itemTypeConfig[item.type || 'obligatoire']?.color}>
                      {itemTypeConfig[item.type || 'obligatoire']?.label}
                    </Badge>
                  </div>

                  {/* Response Type: Conformity */}
                  {(item.responseType === 'conformity' || !item.responseType) && (
                    <div className="flex gap-3 mb-4">
                      <button
                        onClick={() => handleItemResponse(currentSectionIndex, itemIdx, 'conforme')}
                        className={`flex-1 py-3 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                          item.response === 'conforme' 
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                            : 'border-slate-200 hover:border-emerald-300'
                        }`}
                      >
                        <CheckCircle2 className="w-5 h-5" />
                        Conforme
                      </button>
                      <button
                        onClick={() => handleItemResponse(currentSectionIndex, itemIdx, 'non_conforme')}
                        className={`flex-1 py-3 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                          item.response === 'non_conforme' 
                            ? 'border-red-500 bg-red-50 text-red-700' 
                            : 'border-slate-200 hover:border-red-300'
                        }`}
                      >
                        <X className="w-5 h-5" />
                        Non conforme
                      </button>
                      <button
                        onClick={() => handleItemResponse(currentSectionIndex, itemIdx, 'non_applicable')}
                        className={`flex-1 py-3 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                          item.response === 'non_applicable' 
                            ? 'border-slate-500 bg-slate-100 text-slate-700' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <span className="w-5 h-5 flex items-center justify-center">—</span>
                        Non applicable
                      </button>
                    </div>
                  )}

                  {/* Response Type: Checklist */}
                  {item.responseType === 'checklist' && item.subItems && (
                    <div className="space-y-3 mb-4">
                      {item.subItems.map((subItem, subIdx) => (
                        <div key={subIdx} className="flex items-center gap-3">
                          <Checkbox
                            checked={subItem.checked}
                            onCheckedChange={() => handleSubItemToggle(currentSectionIndex, itemIdx, subIdx)}
                          />
                          <span className={subItem.checked ? 'line-through text-slate-400' : ''}>{subItem.text}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Response Type: Rating */}
                  {item.responseType === 'rating' && (
                    <div className="flex gap-1 mb-4">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          onClick={() => handleRating(currentSectionIndex, itemIdx, star)}
                          className="p-1"
                        >
                          <Star className={`w-8 h-8 ${(item.rating || 0) >= star ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Comment Section - Always visible when has comment or toggled */}
                  {(item.showComment || item.comment) && (
                    <Textarea
                      placeholder="Ajoutez un commentaire..."
                      value={item.comment || ''}
                      onChange={(e) => handleItemComment(currentSectionIndex, itemIdx, e.target.value)}
                      className="mb-4"
                      rows={3}
                    />
                  )}

                  {/* Item Photos */}
                  {item.photos && item.photos.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-slate-600 mb-2">Photos ({item.photos.length})</p>
                      <div className="flex gap-2 flex-wrap">
                        {item.photos.map((photo, photoIdx) => (
                          <div key={photoIdx} className="relative group">
                            <img 
                              src={photo} 
                              alt={`Photo ${photoIdx + 1}`}
                              className="w-20 h-20 object-cover rounded-lg border"
                            />
                            <button
                              onClick={() => deletePhoto(currentSectionIndex, itemIdx, photoIdx)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className={`${item.showComment || item.comment ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}`}
                      onClick={() => toggleItemComment(currentSectionIndex, itemIdx)}
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      {item.comment ? 'Modifier' : 'Commentaire'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      onClick={() => openCamera(currentSectionIndex, itemIdx)}
                      data-testid={`photo-btn-${itemIdx}`}
                    >
                      <Camera className="w-4 h-4 mr-1" />Photo
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            {/* Notes */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-blue-500" />
                  Notes générales
                </h3>
                <Textarea
                  placeholder="Ajoutez vos notes ici..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={5}
                  className="resize-none"
                />
              </CardContent>
            </Card>

            {/* Recent Photos */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <Camera className="w-4 h-4 text-blue-500" />
                  Photos récentes
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {recentPhotos.map((photo, idx) => (
                    <div key={idx} className="aspect-square rounded-lg overflow-hidden border">
                      <img 
                        src={photo} 
                        alt={`Recent ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                  <button 
                    onClick={() => {
                      setCurrentPhotoTarget(null);
                      fileInputRef.current?.click();
                    }}
                    className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 border-2 border-dashed hover:bg-slate-50 hover:border-blue-300 transition-colors"
                  >
                    <Plus className="w-6 h-6" />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Actions rapides
                </h3>
                <div className="space-y-2">
                  {progress.reserves > 0 && (
                    <Button variant="outline" className="w-full justify-start">
                      <FileText className="w-4 h-4 mr-2" />
                      Liste des réserves ({progress.reserves})
                    </Button>
                  )}
                  <Button variant="outline" className="w-full justify-start" onClick={() => navigate(`/checklist/report/${id}`)}>
                    <FileText className="w-4 h-4 mr-2" />
                    Aperçu du rapport
                  </Button>
                  <Button 
                    variant="outline" 
                    className={`w-full justify-start ${!isRunning ? 'text-emerald-600' : 'text-red-600'}`}
                    onClick={() => setIsRunning(!isRunning)}
                  >
                    {isRunning ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    {isRunning ? 'Mettre en pause' : 'Reprendre'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${isRunning ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
            <span className="text-sm text-slate-600">
              {isRunning ? 'En cours d\'exécution' : 'En pause'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Enregistrement...' : 'Enregistrer brouillon'}
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => handleSave(true)}
              disabled={saving}
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Terminer le contrôle
            </Button>
          </div>
        </div>
      </div>

      {/* Camera Dialog */}
      <Dialog open={cameraOpen} onOpenChange={closeCamera}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Prendre une photo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {!capturedImage ? (
              <div className="relative aspect-[4/3] bg-black rounded-lg overflow-hidden">
                <video 
                  ref={videoRef}
                  autoPlay 
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                <canvas ref={canvasRef} className="hidden" />
              </div>
            ) : (
              <div className="relative aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden">
                <img 
                  src={capturedImage} 
                  alt="Captured"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            {!capturedImage ? (
              <>
                <Button variant="outline" onClick={closeCamera}>
                  Annuler
                </Button>
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                >
                  <Image className="w-4 h-4 mr-1" />
                  Galerie
                </Button>
                <Button onClick={capturePhoto} className="bg-blue-600 hover:bg-blue-700">
                  <Camera className="w-4 h-4 mr-1" />
                  Capturer
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={retakePhoto}>
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reprendre
                </Button>
                <Button 
                  onClick={savePhoto} 
                  disabled={uploadingPhoto}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                  )}
                  {uploadingPhoto ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
